import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';
import { ACTIONS, ExpiringStorage } from '@oneaudi/stck-store';
import { MandatoryAreaSearchState } from '../types';
import { useServicesContext } from '../../../context';
import { getScsUrlPartsFromConfiguration } from '../../../hooks';
import { FocusLayerSizeV2 } from '@volkswagen-onehub/layer-manager';
import { MasOverlay } from '../components';
import { DynamicThemeProviderWrapper } from '../../utils';
const MANDATORY_AREA_SEARCH_TIMEOUT = 1000;
const MandatoryAreaSearchContext = createContext(undefined);
export const MandatoryAreaSearchProvider = ({ children, }) => {
    var _a;
    const { getConfiguration, getEnvironmentConfig, getFeatureServices } = useServicesContext();
    const { 'layer-manager': layerManager, 'vtp-configuration-service': configurationService, 'audi-content-service': contentService, } = getFeatureServices();
    const { locationFilterConfig_mapsAuthQueryParams } = contentService.getContent().fields;
    const scsUrlParts = getScsUrlPartsFromConfiguration(getConfiguration(), getEnvironmentConfig());
    const localStorageKey = `VTP_${scsUrlParts === null || scsUrlParts === void 0 ? void 0 : scsUrlParts.marketPath}_mandatoryLocationSearch--input`;
    const localStorage = new ExpiringStorage(localStorageKey, 30);
    const MASinputDataFromLocalStorage = localStorage.readEntry();
    const enableMandatoryAreaSearch = ((_a = configurationService === null || configurationService === void 0 ? void 0 : configurationService.getConfiguration()) === null || _a === void 0 ? void 0 : _a.enableMandatoryAreaSearch) || false;
    const dispatch = useDispatch();
    const [mandatoryAreaSearchState, setMandatoryAreaSearchState] = useState(() => enableMandatoryAreaSearch ? MandatoryAreaSearchState.ENABLED : MandatoryAreaSearchState.DISABLED);
    const [mandatoryAreaSearchModalNode, setMandatoryAreaSearchModalNode] = useState(null);
    const [mandatoryAreaSearchModal, setMandatoryAreaSearchModal] = useState(null);
    const mandatoryAreaSearchModalRef = useCallback((node) => {
        setMandatoryAreaSearchModalNode(node);
    }, []);
    const openMandatoryAreaSearchModal = useCallback(() => {
        const modal = layerManager.openFocusLayer(() => React.createElement("div", { id: "mas-layer-id", ref: mandatoryAreaSearchModalRef }), {}, { size: FocusLayerSizeV2.A, userCloseable: false });
        setMandatoryAreaSearchModal(modal);
    }, [layerManager, mandatoryAreaSearchModalRef]);
    const closeMandatoryAreaSearchModal = useCallback(() => {
        mandatoryAreaSearchModal === null || mandatoryAreaSearchModal === void 0 ? void 0 : mandatoryAreaSearchModal.close();
        setMandatoryAreaSearchState(MandatoryAreaSearchState.LOCATION_SET);
        dispatch(ACTIONS.UI.setLoader({ state: false, feature: 'tiles' }));
    }, [mandatoryAreaSearchModal, dispatch]);
    const setMandatoryAreaSearchLocationSelected = useCallback(() => {
        if (!enableMandatoryAreaSearch) {
            return;
        }
        setMandatoryAreaSearchState(MandatoryAreaSearchState.LOCATION_SET);
        dispatch(ACTIONS.UI.setLoader({ state: false, feature: 'tiles' }));
    }, [dispatch, enableMandatoryAreaSearch]);
    const requireMandatoryAreaSearchLocation = useCallback(() => {
        if (!enableMandatoryAreaSearch) {
            return;
        }
        setMandatoryAreaSearchState(MandatoryAreaSearchState.ENABLED);
        dispatch(ACTIONS.UI.setLoader({ state: false, feature: 'tiles' }));
    }, [dispatch, enableMandatoryAreaSearch]);
    useEffect(() => {
        if (mandatoryAreaSearchState === MandatoryAreaSearchState.ENABLED) {
            if (!MASinputDataFromLocalStorage) {
                dispatch(ACTIONS.UI.setLoader({ state: true, feature: 'tiles' }));
                setTimeout(openMandatoryAreaSearchModal, MANDATORY_AREA_SEARCH_TIMEOUT);
            }
            else {
                setMandatoryAreaSearchState(MandatoryAreaSearchState.LOCATION_SET);
            }
        }
    }, []);
    const contextValue = useMemo(() => ({
        enableMandatoryAreaSearch,
        mandatoryAreaSearchState,
        isMandatoryAreaSearchModalOpen: !!mandatoryAreaSearchModalNode,
        openMandatoryAreaSearchModal,
        setMandatoryAreaSearchLocationSelected,
        requireMandatoryAreaSearchLocation,
    }), [
        enableMandatoryAreaSearch,
        mandatoryAreaSearchState,
        mandatoryAreaSearchModalNode,
        openMandatoryAreaSearchModal,
        setMandatoryAreaSearchLocationSelected,
        requireMandatoryAreaSearchLocation,
    ]);
    return (React.createElement(MandatoryAreaSearchContext.Provider, { value: contextValue },
        children,
        !!locationFilterConfig_mapsAuthQueryParams &&
            enableMandatoryAreaSearch &&
            mandatoryAreaSearchModalNode &&
            ReactDOM.createPortal(React.createElement(DynamicThemeProviderWrapper, null,
                React.createElement(MasOverlay, { closeMasOverlay: closeMandatoryAreaSearchModal })), mandatoryAreaSearchModalNode)));
};
export const useMandatoryAreaSearchContext = () => {
    const context = useContext(MandatoryAreaSearchContext);
    if (!context) {
        throw new Error('useMandatoryAreaSearchContext must be used within a MandatoryAreaSearchProvider');
    }
    return context;
};
